// frontend/src/components/QuantixGo/TransportCostsPage.jsx
import React, { useState, useEffect } from 'react';
import { quantixgoAPI } from '../../api/client';
import { useToast } from '../../hooks/useToast';
import { useTranslation } from '../../context/TranslationContext';
import DataTable from '../Common/DataTable';
import './DeviceRequestsPage.css';

const EMPTY_FORM = {
  subsidiary_code: '', currency_code: '', gen_bus_posting_group: '',
  min_order_amount: '0', max_order_amount: '', cost_amount: '', account_code: '', description: '', is_active: true,
};

const TransportCostsPage = () => {
  const { t } = useTranslation();
  const { toast_success, toast_error } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', row? }
  const [form, setForm] = useState(EMPTY_FORM);
  const [subsidiaryFilter, setSubsidiaryFilter] = useState('');

  const loadRows = async () => {
    try {
      setLoading(true);
      const res = await quantixgoAPI.listTransportCosts();
      setRows(res.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading transport costs:', err);
      setError(t('quantixgo.transport.loadError', 'Falha ao carregar'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); }, []);

  const filteredRows = subsidiaryFilter
    ? rows.filter((r) => r.subsidiary_code === subsidiaryFilter)
    : rows;

  const subsidiaryOptions = [...new Set(rows.map((r) => r.subsidiary_code))].sort();

  const openCreate = () => { setForm(EMPTY_FORM); setModal({ mode: 'create' }); };
  const openEdit = (row) => {
    setForm({
      subsidiary_code: row.subsidiary_code,
      currency_code: row.currency_code || '',
      gen_bus_posting_group: row.gen_bus_posting_group || '',
      min_order_amount: String(row.min_order_amount),
      max_order_amount: row.max_order_amount != null ? String(row.max_order_amount) : '',
      cost_amount: String(row.cost_amount),
      account_code: row.account_code,
      description: row.description,
      is_active: row.is_active,
    });
    setModal({ mode: 'edit', row });
  };

  const handleSave = async () => {
    if (!form.subsidiary_code || form.cost_amount === '' || !form.account_code || !form.description) {
      toast_error(t('quantixgo.transport.missingFields', 'Preencha os campos obrigatórios (subsidiária, custo, conta, descrição)'));
      return;
    }

    const payload = {
      subsidiary_code: form.subsidiary_code,
      currency_code: form.currency_code || null,
      gen_bus_posting_group: form.gen_bus_posting_group || null,
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      max_order_amount: form.max_order_amount === '' ? null : parseFloat(form.max_order_amount),
      cost_amount: parseFloat(form.cost_amount),
      account_code: form.account_code,
      description: form.description,
      is_active: form.is_active,
    };

    try {
      if (modal.mode === 'create') {
        await quantixgoAPI.createTransportCost(payload);
        toast_success(t('quantixgo.transport.created', 'Regra criada'));
      } else {
        await quantixgoAPI.updateTransportCost(modal.row.transport_cost_id, payload);
        toast_success(t('quantixgo.transport.updated', 'Regra atualizada'));
      }
      setModal(null);
      loadRows();
    } catch (err) {
      toast_error(err.response?.data?.error || t('quantixgo.transport.saveError', 'Falha ao guardar'));
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('quantixgo.transport.confirmDelete', `Eliminar a regra "${row.description}" (${row.subsidiary_code})?`))) return;
    try {
      await quantixgoAPI.deleteTransportCost(row.transport_cost_id);
      toast_success(t('quantixgo.transport.deleted', 'Regra eliminada'));
      loadRows();
    } catch (err) {
      toast_error(err.response?.data?.error || t('quantixgo.transport.deleteError', 'Falha ao eliminar'));
    }
  };

  const columns = [
    { header: t('quantixgo.transport.subsidiary', 'Subsidiária'), field: 'subsidiary_code' },
    {
      header: t('quantixgo.transport.currency', 'Moeda'),
      field: 'currency_code',
      render: (row) => row.currency_code || <span className="text-muted">(branco)</span>,
    },
    {
      header: t('quantixgo.transport.postingGroup', 'Posting Group'),
      field: 'gen_bus_posting_group',
      render: (row) => row.gen_bus_posting_group || <span className="text-muted">—</span>,
    },
    {
      header: t('quantixgo.transport.range', 'Intervalo'),
      field: 'range',
      render: (row) => `${parseFloat(row.min_order_amount).toFixed(2)} — ${row.max_order_amount != null ? parseFloat(row.max_order_amount).toFixed(2) : '∞'}`,
    },
    {
      header: t('quantixgo.transport.cost', 'Custo'),
      field: 'cost_amount',
      className: 'text-right',
      render: (row) => parseFloat(row.cost_amount).toFixed(2),
    },
    { header: t('quantixgo.transport.account', 'Conta'), field: 'account_code' },
    { header: t('quantixgo.transport.description', 'Descrição'), field: 'description' },
    {
      header: t('common.status', 'Estado'),
      field: 'is_active',
      render: (row) => (
        <span className={`status-badge ${row.is_active ? 'status-completed' : 'status-cancelled'}`}>
          {row.is_active ? t('common.active', 'Ativo') : t('common.inactive', 'Inativo')}
        </span>
      ),
    },
    {
      header: '',
      field: 'actions',
      className: 'text-right',
      render: (row) => (
        <>
          <button className="btn-search" onClick={() => openEdit(row)} style={{ marginRight: 6 }}>
            {t('common.edit', 'Editar')}
          </button>
          <button className="btn-danger" onClick={() => handleDelete(row)}>
            {t('common.delete', 'Eliminar')}
          </button>
        </>
      ),
    },
  ];

  return (
    <div className="order-list device-requests-list">
      <div className="order-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>{t('quantixgo.transport.subsidiary', 'Subsidiária')}</label>
            <select value={subsidiaryFilter} onChange={(e) => setSubsidiaryFilter(e.target.value)}>
              <option value="">{t('common.all', 'Todas')}</option>
              {subsidiaryOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button className="btn-search" onClick={openCreate}>
            + {t('quantixgo.transport.new', 'Nova Regra')}
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredRows}
        loading={loading}
        error={error}
        onRetry={loadRows}
        emptyMessage={t('quantixgo.transport.none', 'Nenhuma regra definida')}
      />

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{modal.mode === 'create' ? t('quantixgo.transport.new', 'Nova Regra') : t('common.edit', 'Editar')}</h3>

            <div className="form-group">
              <label>{t('quantixgo.transport.subsidiary', 'Subsidiária')} *</label>
              <input value={form.subsidiary_code} onChange={(e) => setForm({ ...form, subsidiary_code: e.target.value })} placeholder="ex: IC-PT" />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.currency', 'Moeda')} ({t('quantixgo.transport.currencyHint', 'vazio = qualquer moeda / branco')})</label>
              <input value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value })} placeholder="ex: DKK, SEK (deixar vazio para branco)" />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.postingGroup', 'Gen. Bus. Posting Group')} ({t('quantixgo.transport.postingGroupHint', 'vazio = qualquer grupo')})</label>
              <input value={form.gen_bus_posting_group} onChange={(e) => setForm({ ...form, gen_bus_posting_group: e.target.value })} placeholder="ex: FR_NAT (deixar vazio se não aplicável)" />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.minAmount', 'Valor mínimo (inclusive)')}</label>
              <input type="number" step="0.01" value={form.min_order_amount} onChange={(e) => setForm({ ...form, min_order_amount: e.target.value })} />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.maxAmount', 'Valor máximo (inclusive, vazio = sem limite)')}</label>
              <input type="number" step="0.01" value={form.max_order_amount} onChange={(e) => setForm({ ...form, max_order_amount: e.target.value })} />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.cost', 'Custo de transporte')} *</label>
              <input type="number" step="0.01" value={form.cost_amount} onChange={(e) => setForm({ ...form, cost_amount: e.target.value })} />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.account', 'Nº de Conta Contabilística')} *</label>
              <input value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} />
            </div>

            <div className="form-group">
              <label>{t('quantixgo.transport.description', 'Descrição (linha na encomenda)')} *</label>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="ex: Portes, Frais de transport" />
            </div>

            <div className="form-group">
              <label>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                {' '}{t('common.active', 'Ativo')}
              </label>
            </div>

            <div className="modal-actions">
              <button className="btn-search" onClick={handleSave}>{t('common.save', 'Guardar')}</button>
              <button className="btn-secondary" onClick={() => setModal(null)}>{t('common.cancel', 'Cancelar')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportCostsPage;